import React, { useState } from 'react';
import type { CharacterPortrait, TextOverlayScene } from '../../types';
import { AssetPickerModal } from './AssetPickerModal';
import { assetUrl } from './scenario-api';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';

interface InlineTextOverlayEditorProps {
  item: TextOverlayScene;
  editMode: boolean;
  scenarioId: string;
  characters: CharacterPortrait[];
  onChange: (item: TextOverlayScene) => void;
}

function InlineLetterSenderMeta({
  characterName,
  characterIcon,
  characters,
  scenarioId,
  onChange,
}: {
  characterName?: string;
  characterIcon?: string;
  characters: CharacterPortrait[];
  scenarioId: string;
  onChange: (patch: { characterName?: string; characterIcon?: string }) => void;
}) {
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  const applyCharacter = (characterId: string) => {
    if (!characterId) return;
    const char = characters.find((c) => c.id === characterId);
    if (!char) return;
    onChange({
      characterName: char.name,
      characterIcon: char.icon || char.defaultImage,
    });
  };

  return (
    <div className={inline.overlaySenderEditor}>
      <div className={inline.phoneConvContactRow}>
        {characterIcon ? (
          <img
            className={inline.phoneConvContactAvatarThumb}
            src={assetUrl(scenarioId, characterIcon)}
            alt=""
          />
        ) : (
          <div className={inline.phoneConvContactAvatarPlaceholder} aria-hidden />
        )}

        <div className={`${inline.metaField} ${inline.phoneConvContactNameField}`}>
          <span className={inline.metaFieldLabel}>Nom du personnage</span>
          <input
            className={studio.input}
            value={characterName ?? ''}
            onChange={(e) => onChange({ characterName: e.target.value || undefined })}
            placeholder="Natalie — Direction"
          />
        </div>

        <div className={`${inline.metaField} ${inline.phoneConvContactAvatarField}`}>
          <span className={inline.metaFieldLabel}>Icône</span>
          <div className={inline.phoneConvContactAvatarInputRow}>
            <input
              className={studio.input}
              value={characterIcon ?? ''}
              onChange={(e) => onChange({ characterIcon: e.target.value || undefined })}
              placeholder="assets/characters/…"
            />
            <button
              type="button"
              className={studio.btnSecondary}
              onClick={() => setIconPickerOpen(true)}
            >
              Parcourir
            </button>
          </div>
        </div>
      </div>

      {characters.length > 0 && (
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Depuis personnage</span>
          <select
            className={studio.select}
            value=""
            onChange={(e) => applyCharacter(e.target.value)}
          >
            <option value="">— Choisir —</option>
            {characters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.id}
              </option>
            ))}
          </select>
        </div>
      )}

      {iconPickerOpen && (
        <AssetPickerModal
          scenarioId={scenarioId}
          value={characterIcon ?? ''}
          subdir="characters"
          onSelect={(path) => {
            onChange({ characterIcon: path });
            setIconPickerOpen(false);
          }}
          onClose={() => setIconPickerOpen(false)}
        />
      )}
    </div>
  );
}

export const InlineTextOverlayEditor: React.FC<InlineTextOverlayEditorProps> = ({
  item,
  editMode,
  scenarioId,
  characters,
  onChange,
}) => {
  if (!editMode) {
    return (
      <div className={inline.overlayReadonly}>
        {(item.characterName || item.characterIcon) && (
          <div className={inline.overlaySenderPreview}>
            {item.characterIcon && (
              <img
                className={inline.overlaySenderIcon}
                src={assetUrl(scenarioId, item.characterIcon)}
                alt=""
              />
            )}
            {item.characterName && (
              <span className={inline.overlaySenderName}>{item.characterName}</span>
            )}
          </div>
        )}
        {item.title && <span className={inline.overlayTitle}>{item.title}</span>}
        {(item.overlayType ?? 'letter') === 'letter' && item.channelLabel?.trim() && (
          <span className={inline.overlayChannelLabel}>{item.channelLabel.trim()}</span>
        )}
        {item.overlayType === 'cyber' && (
          <span className={inline.overlayChannelLabel}>
            {item.channelLabel?.trim() || 'Canal sécurisé'}
          </span>
        )}
        <span className={inline.slText}>{item.content || '…'}</span>
        {item.continueText && (
          <span className={inline.overlayContinuePreview}>{item.continueText}</span>
        )}
      </div>
    );
  }

  return (
    <div className={inline.overlayEditor}>
      <div className={inline.slMetaRow}>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>overlayType</span>
          <select
            className={studio.select}
            value={item.overlayType ?? 'letter'}
            onChange={(e) =>
              onChange({
                ...item,
                overlayType: e.target.value as TextOverlayScene['overlayType'],
              })
            }
          >
            <option value="letter">letter</option>
            <option value="cyber">cyber</option>
          </select>
        </div>
      </div>
      <InlineLetterSenderMeta
        characterName={item.characterName}
        characterIcon={item.characterIcon}
        characters={characters}
        scenarioId={scenarioId}
        onChange={(patch) => onChange({ ...item, ...patch })}
      />
      {((item.overlayType ?? 'letter') === 'letter' || item.overlayType === 'cyber') && (
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Libellé canal</span>
          <input
            type="text"
            className={studio.input}
            value={item.channelLabel ?? ''}
            placeholder={(item.overlayType ?? 'letter') === 'cyber' ? 'Canal sécurisé' : 'Mission 1'}
            onChange={(e) =>
              onChange({ ...item, channelLabel: e.target.value.trim() || undefined })
            }
          />
        </div>
      )}
      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Titre</span>
        <input
          type="text"
          className={studio.input}
          value={item.title ?? ''}
          placeholder="Ex. Natalie — Direction"
          onChange={(e) => onChange({ ...item, title: e.target.value || undefined })}
        />
      </div>
      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Contenu</span>
        <textarea
          className={inline.overlayContent}
          rows={4}
          value={item.content}
          placeholder="Texte de la lettre…"
          onChange={(e) => onChange({ ...item, content: e.target.value })}
        />
      </div>
      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Libellé du bouton continuer</span>
        <input
          type="text"
          className={studio.input}
          value={item.continueText ?? ''}
          placeholder="Commencer la mission"
          onChange={(e) =>
            onChange({ ...item, continueText: e.target.value.trim() || undefined })
          }
        />
      </div>
    </div>
  );
};
